/**
 * Iter 64 — Daily Backup dashboard.
 *
 * Lists every gzipped DB snapshot ever taken (last 30 kept). Super Admins
 * can trigger an on-demand backup, download any snapshot, or delete one.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Database, Download, Trash2, RefreshCw, Loader2, PlayCircle, CalendarClock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { useConfirm } from '../../components/ConfirmDialog';
import { apiGet, apiPost, apiDel, API } from '../../lib/api';

import { formatDateTime as fmt } from '../../lib/format';

function fmtMb(bytes) {
  if (!bytes) return '0 MB';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
}

export default function Backups() {
  const navigate = useNavigate();
  const [confirm, ConfirmHost] = useConfirm();
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/settings/backups');
      setRows(r.rows || []);
      setHealth(r.health || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load backups');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // (§3) Manual backup is async: kick it off, then poll the run for progress
  // and final outcome. A still-running backup is never reported as an error.
  const runNow = async () => {
    setRunning(true);
    setProgress({ current_collection: null, collections_done: 0, collections_total: 0, documents_read: 0 });
    try {
      const r = await apiPost('/settings/backups/now', {});
      const runId = r.run_id;
      const poll = async () => {
        try {
          const run = await apiGet(`/settings/backups/run/${runId}`);
          if (run.status === 'running') {
            setProgress(run.progress || null);
            setTimeout(poll, 2000);
          } else if (run.status === 'success') {
            setProgress(null); setRunning(false);
            toast.success(`Backup complete · ${(run.size_compressed_bytes ? (run.size_compressed_bytes / 1024 / 1024).toFixed(2) : '?')} MB · ${run.total_documents?.toLocaleString?.() || run.total_documents} docs`);
            load();
          } else {
            setProgress(null); setRunning(false);
            const d = run.diagnostics;
            toast.error(`Backup failed${d?.failed_during_collection ? ` on "${d.failed_during_collection}"` : ''}: ${run.error || 'unknown error'}`);
            load();
          }
        } catch (e) {
          setRunning(false); setProgress(null);
          toast.error('Lost track of the backup run — check the list to see if it completed.');
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      setRunning(false); setProgress(null);
      toast.error(e?.response?.data?.detail || 'Failed to start backup');
    }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: 'Delete this backup?',
      body: `Backup taken on ${fmt(row.created_at)} (${fmtMb(row.size_compressed_bytes)}, ${row.total_documents.toLocaleString()} documents) will be permanently removed.\n\nThis action cannot be undone.`,
      confirmLabel: 'Delete Backup',
      danger: true,
    });
    if (!ok) return;
    try {
      await apiDel(`/settings/backups/${row.id}`);
      toast.success('Backup deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const onDownload = (row) => {
    // Use raw fetch+blob so we keep auth header
    const token = localStorage.getItem('access_token');
    fetch(`${API}/settings/backups/${row.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ellora-backup-${row.created_at.slice(0, 10)}-${row.id.slice(0, 8)}.json.gz`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download failed'));
  };

  return (
    <div className="p-6 md:p-8" data-testid="backups-page">
      {ConfirmHost}
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Data" title="Database Backups" subtitle="Daily snapshot @ 02:00 IST · last 30 retained · gzipped JSON of every collection." icon={Database} />

      {health && (health.alert ? (
        <div className="mt-6 rounded-sm border border-rose-300 bg-rose-50 p-4 flex items-start gap-3" data-testid="backup-health-alert" role="alert">
          <AlertTriangle size={18} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-display font-bold text-rose-800">Backup needs attention</div>
            <div className="text-xs text-rose-700 mt-0.5 break-words">{health.alert}</div>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-sm border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2.5" data-testid="backup-health-ok">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <div className="text-xs text-emerald-800">
            Backups healthy{health.hours_since_last != null ? ` · last successful backup ${health.hours_since_last}h ago` : ''}.
          </div>
        </div>
      ))}

      <div className="bg-gradient-to-br from-[#3b4872] to-[#0F172A] text-white rounded-sm p-5 mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="schedule-card">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-display font-bold flex items-center gap-1.5">
            <CalendarClock size={11} /> Schedule
          </div>
          <div className="text-lg font-display font-bold mt-0.5">Automatic daily backup at 02:00 IST</div>
          <div className="text-xs text-white/70 mt-0.5">Triggered by APScheduler · stored gzipped in MongoDB · retention 30 backups · superseded older ones auto-pruned.</div>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          className="bg-white text-[#3b4872] px-4 py-2 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="run-backup-now"
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
          {running ? 'Backing up…' : 'Run Backup Now'}
        </button>
      </div>

      {running && progress && (
        <div className="mt-3 rounded-sm border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-900 flex items-center gap-2" data-testid="backup-progress">
          <Loader2 size={13} className="animate-spin shrink-0" />
          <span>
            Backing up… {progress.collections_done || 0}/{progress.collections_total || '?'} collections
            {progress.current_collection ? ` · reading "${progress.current_collection}"` : ''}
            {typeof progress.documents_read === 'number' ? ` · ${progress.documents_read.toLocaleString()} documents read so far` : ''}.
            You can leave this page — the backup keeps running.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-slate-500">
          {rows.length} backup{rows.length === 1 ? '' : 's'} on disk
          {rows.length > 0 && ` · most recent ${fmt(rows[0]?.created_at)}`}
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-indigo-50 flex items-center gap-1 font-display font-bold uppercase tracking-wider" data-testid="refresh-btn">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-3">
        {loading ? (
          <div className="py-14 text-center text-sm text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-500">No backups yet. Click "Run Backup Now" to create the first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
              <tr>
                <th className="text-left py-2.5 px-3 w-44">Created</th>
                <th className="text-left py-2.5 px-3 w-20">Trigger</th>
                <th className="text-left py-2.5 px-3 w-32">Documents</th>
                <th className="text-left py-2.5 px-3 w-24">Size</th>
                <th className="text-left py-2.5 px-3">Initiated by</th>
                <th className="text-left py-2.5 px-3 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t border-slate-100 ${r.usable === false ? 'bg-rose-50/50' : ''}`} data-testid={`backup-row-${r.id}`}>
                  <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm ${r.trigger === 'scheduled' ? 'bg-indigo-50 text-[#3b4872]' : 'bg-amber-50 text-amber-800'}`}>
                      {r.trigger}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs">{r.total_documents?.toLocaleString() || 0}</td>
                  <td className="py-2 px-3 text-xs tabular-nums">{fmtMb(r.size_compressed_bytes)}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{r.initiated_by}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      {r.usable === false ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-bold uppercase rounded-sm bg-rose-100 text-rose-700"
                          title="Stored data is missing or incomplete — this archive cannot be restored."
                          data-testid={`unusable-${r.id}`}
                        >
                          <AlertTriangle size={10} /> Unusable
                        </span>
                      ) : (
                        <button onClick={() => onDownload(r)} className="px-2.5 py-1 text-[10px] font-display font-bold uppercase bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] flex items-center gap-1" data-testid={`dl-${r.id}`}>
                          <Download size={10} /> Download
                        </button>
                      )}
                      <button onClick={() => onDelete(r)} className="text-rose-500 hover:text-rose-700" title="Delete" data-testid={`del-${r.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
